"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppDerivedSourceSchema = exports.AppBaseSourceQuerySchema = exports.DatabaseViewFilterGroupSchema = exports.APP_DERIVED_SOURCES_MAX = exports.appBaseLinkedSteps = exports.APP_BASE_SOURCE_QUERY_MAX_WINDOWS = exports.APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT = exports.APP_BASE_MAX_DATASET_ROWS = exports.APP_BASE_LINKED_DEPTH = void 0;
const database_identity_1 = require("@streamlain/database-identity");
const zod_1 = require("zod");
const app_base_bridge_budgets_1 = require("./app-base-bridge-budgets");
Object.defineProperty(exports, "APP_BASE_LINKED_DEPTH", { enumerable: true, get: function () { return app_base_bridge_budgets_1.APP_BASE_LINKED_DEPTH; } });
Object.defineProperty(exports, "APP_BASE_MAX_DATASET_ROWS", { enumerable: true, get: function () { return app_base_bridge_budgets_1.APP_BASE_MAX_DATASET_ROWS; } });
Object.defineProperty(exports, "APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT", { enumerable: true, get: function () { return app_base_bridge_budgets_1.APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT; } });
Object.defineProperty(exports, "APP_BASE_SOURCE_QUERY_MAX_WINDOWS", { enumerable: true, get: function () { return app_base_bridge_budgets_1.APP_BASE_SOURCE_QUERY_MAX_WINDOWS; } });
const app_base_linked_from_1 = require("./app-base-linked-from");
Object.defineProperty(exports, "appBaseLinkedSteps", { enumerable: true, get: function () { return app_base_linked_from_1.appBaseLinkedSteps; } });
/**
 * The query of an App source as data, without the executor
 * (app-base-source-query.ts): the manifest holds it in its derived sources,
 * so this file imports nothing of the App contract.
 */
/** The derived sources of one manifest. */
exports.APP_DERIVED_SOURCES_MAX = 64;
const columnId = zod_1.z.string().min(1).max(256);
const predicate = zod_1.z.lazy(() => zod_1.z.union([
    zod_1.z
        .object({
        type: zod_1.z.literal('predicate-group'),
        operator: zod_1.z.enum(['and', 'or']),
        predicates: zod_1.z.array(predicate).max(32),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('text-search'),
        query: zod_1.z.string().max(500),
        propertyIds: zod_1.z.array(columnId).max(64).optional(),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('column'),
        propertyId: columnId,
        operator: zod_1.z.enum([
            'boolean-is',
            'contains',
            'equals',
            'is-empty',
            'is-not-empty',
            'not-equals',
        ]),
        value: zod_1.z.unknown().optional(),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('temporal-overlap'),
        startPropertyId: columnId,
        endPropertyId: columnId.optional(),
        windowStart: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
        windowEnd: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
    })
        .strict(),
]));
const filter = zod_1.z.lazy(() => zod_1.z
    .object({
    type: zod_1.z.literal('group'),
    op: zod_1.z.enum(['and', 'or']),
    conditions: zod_1.z
        .array(zod_1.z.union([
        filter,
        zod_1.z
            .object({
            type: zod_1.z.literal('filter'),
            left: zod_1.z
                .object({ type: zod_1.z.literal('ref'), name: columnId })
                .strict(),
            function: zod_1.z.string().min(1),
            args: zod_1.z
                .array(zod_1.z
                .object({
                type: zod_1.z.literal('literal'),
                value: zod_1.z.unknown().optional(),
            })
                .strict())
                .max(32),
        })
            .strict(),
    ]))
        .max(32),
})
    .strict());
/**
 * A filter group of the database kernel whose every function the kernel
 * knows, with its arguments: the kernel lets an unknown function match every
 * row, so a declared filter is checked here instead.
 */
exports.DatabaseViewFilterGroupSchema = filter.superRefine((group, ctx) => {
    for (const diagnostic of (0, database_identity_1.normalizeDatabaseProjectionFilter)(group)
        .diagnostics)
        ctx.addIssue({
            code: 'custom',
            message: diagnostic.message,
            path: (diagnostic.path ?? '')
                .split('.')
                .slice(1)
                .map(part => (/^\d+$/.test(part) ? Number(part) : part)),
        });
});
const linkedOrigin = {
    sourceKey: zod_1.z.string().min(1).max(256),
    columnId,
};
const linkedFrom = zod_1.z.lazy(() => zod_1.z.union([
    zod_1.z
        .object({
        ...linkedOrigin,
        rowIds: zod_1.z.array(columnId).transform(ids => [...new Set(ids)]),
    })
        .strict(),
    zod_1.z
        .object({
        ...linkedOrigin,
        predicates: zod_1.z.array(predicate).max(32),
        filter: filter.optional(),
    })
        .strict(),
    zod_1.z.object({ ...linkedOrigin, linkedFrom }).strict(),
]));
const AppBaseSourceQueryObjectSchema = zod_1.z
    .object({
    offset: zod_1.z.number().int().nonnegative().safe().default(0),
    limit: zod_1.z
        .number()
        .int()
        .positive()
        .safe()
        .default(app_base_bridge_budgets_1.APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT),
    rowIds: zod_1.z
        .array(columnId)
        .transform(ids => [...new Set(ids)])
        .optional(),
    /** Only rows the server records as owned by the requesting principal.
     * Server readers resolve it to rowIds before execution; local replicas
     * carry no ownership and reject it. */
    owner: zod_1.z.literal('me').optional(),
    /** The rows of this source that origin rows link (AppBaseLinkedFrom).
     * Hosts resolve it to rowIds with resolveAppBaseLinkedFrom before
     * execution, reading the origin rows of every step with the same rights
     * on the same route. */
    linkedFrom: linkedFrom.optional(),
    columnIds: zod_1.z
        .array(columnId)
        .max(64)
        .transform(ids => [...new Set(ids)])
        .optional(),
    /** Rows named by `rowIds` or `linkedFrom` are details with every
     * exposed column; `'selected'` gives them the `columnIds` only, as a
     * list window does. */
    rowColumns: zod_1.z.literal('selected').optional(),
    search: zod_1.z.string().max(500).optional(),
    searchColumnIds: zod_1.z.array(columnId).max(64).optional(),
    predicates: zod_1.z.array(predicate).max(32).default([]),
    filter: filter.optional(),
    sort: zod_1.z
        .array(zod_1.z.union([
        zod_1.z
            .object({
            type: zod_1.z.literal('property').optional(),
            propertyId: columnId,
            direction: zod_1.z.enum(['asc', 'desc']).default('asc'),
        })
            .strict(),
        zod_1.z
            .object({
            columnId,
            direction: zod_1.z.enum(['asc', 'desc']).default('asc'),
        })
            .strict()
            .transform(entry => ({
            propertyId: entry.columnId,
            direction: entry.direction,
        })),
    ]))
        .max(8)
        .default([]),
    aggregates: zod_1.z
        .array(zod_1.z
        .object({
        id: zod_1.z.string().min(1).max(128),
        type: zod_1.z.enum(['count', 'sum', 'min', 'max', 'avg']),
        propertyId: columnId.optional(),
    })
        .strict())
        .max(16)
        .default([]),
    groupBy: zod_1.z
        .array(columnId)
        .max(2)
        .transform(ids => [...new Set(ids)])
        .default([]),
    /** Bridge-internal, never App input: the delta token of a result the
     * reader holds in full. The window then carries only rows of shards
     * changed since and `order` names all its rows, or `sameOrder` says
     * they are the rows of the token in its order (see
     * createAppBaseSourceQueryDeltas); a token the host does not know reads
     * the window in full. */
    since: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    /** Bridge-internal, with `since`: the reader holds the ids of the
     * token's window in order, so a window in that order may answer
     * `sameOrder` instead of `order`. A reader that does not say so (one
     * a page loaded before) always gets `order`. */
    sameOrder: zod_1.z.literal(true).optional(),
    /** Bridge-internal: the further windows of a read, answered with this
     * window on the same state, so a read asks once (see
     * executePortableAppBaseSourceQueryWindows). With `since`, the windows
     * of a read the reader holds, each with the delta token it got for it;
     * without, the windows of a complete read that follow this one in
     * order, each as large as this one. */
    windows: zod_1.z
        .array(zod_1.z
        .object({
        offset: zod_1.z.number().int().nonnegative().safe(),
        limit: zod_1.z.number().int().positive().safe(),
        since: zod_1.z
            .string()
            .regex(/^[a-f0-9]{64}$/)
            .optional(),
    })
        .strict())
        .min(1)
        .max(app_base_bridge_budgets_1.APP_BASE_SOURCE_QUERY_MAX_WINDOWS)
        .optional(),
})
    .strict();
exports.AppBaseSourceQuerySchema = AppBaseSourceQueryObjectSchema.refine(query => !query.rowColumns || query.columnIds !== undefined, {
    message: 'rowColumns selects the columnIds; it needs them',
    path: ['rowColumns'],
})
    .refine(query => !query.linkedFrom ||
    (0, app_base_linked_from_1.appBaseLinkedSteps)(query.linkedFrom).length <= app_base_bridge_budgets_1.APP_BASE_LINKED_DEPTH, {
    message: `linkedFrom reads across at most ${app_base_bridge_budgets_1.APP_BASE_LINKED_DEPTH} relations`,
    path: ['linkedFrom'],
})
    .refine(query => !query.windows ||
    (query.since
        ? query.windows.every(window => window.since !== undefined)
        : query.windows.every((window, index) => window.since === undefined &&
            window.limit === query.limit &&
            window.offset === query.offset + (index + 1) * query.limit)), {
    message: 'Further windows need the delta token of the first, or follow the first window of a complete read in order',
    path: ['windows'],
});
/**
 * A derived source: the result of one query over exactly one source of the
 * manifest, read like a source with readWindow. The reader computes it with
 * executePortableAppBaseSourceQuery; the bridge-internal fields, the owner
 * filter (only server readers resolve it), linkedFrom (hosts resolve it
 * per request) and rowColumns (a list read by row IDs of a page) are no part
 * of a definition.
 */
exports.AppDerivedSourceSchema = zod_1.z
    .object({
    key: zod_1.z.string().min(1).max(256),
    sourceKey: zod_1.z.string().min(1),
    query: AppBaseSourceQueryObjectSchema.omit({
        since: true,
        sameOrder: true,
        windows: true,
        owner: true,
        linkedFrom: true,
        rowColumns: true,
    }),
})
    .strict();
